
import React, { Component, createRef } from 'react';
import { Button } from 'primereact/button';
import { Input, Spinner } from 'reactstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { debounce, groupBy, isEqual, orderBy } from 'lodash';

import ManagerContext from '../context/ManagerContext';
import { JobProgressIndicator } from './JobProgressIndicator';
import { UtilsService } from '../services/UtilsService';
import { ApiService } from '../services/ApiService';
import { ActiveJobs } from './ActiveJobs';

import './ActiveJobsByDriver.css';


// interface ActiveJobsByDriverProps {
//     activeJobs: Job[];
//     loadingActiveJobs?: boolean;
//     scrollable?: boolean;
//     driverNumber?: number;
//     jobSelected?: Job;
//     driverExpanded?: Driver;
//     driverNumberFilterChanged?: number;
// }



export class ActiveJobsByDriver extends Component {

    utilsService = new UtilsService();
    apiService = new ApiService();
    displayName = ActiveJobsByDriver.name;
    driversTable = createRef();
    driverFilterInput = createRef();
    

    constructor(props, context) {
        super(props, context);

        this.state = {
            context: context,
            expandedRows: [],
            sortField: 'pickupDistance',
            sortOrder: -1,
            allocatedJobsCount: null,
            pickedUpJobsCount: null,
            completedJobsCount: null,
            remainingJobsCount: null,
        };
    }


    componentDidMount() {
        setTimeout(() => {
            this.computeStatusSummaryCount();
        });
    }
    
    
    componentDidUpdate(prevProps, prevState) {
        if(prevProps.driverNumber !== this.props.driverNumber) {
            this.setFilters({ driverNumber: this.props?.driverNumber });
        }

        if(prevProps.activeJobs !== this.props.activeJobs) {
            setTimeout(() => {
                this.computeStatusSummaryCount();
            });
        }
    }


    emitDriverNumberFilterChanged = debounce(
        (filterValue) => {
            return this.props.driverNumberFilterChanged(filterValue);
        }, 250
    );


    driverTableColumns(drivers) {
        const columns = [
            {
                field: 'driverNumber', header: 'Driver', sortable: true, width: '65px', filter: true,
                filterElement: <Input innerRef={(e) => this.driverFilterInput = e} onKeyDown={(e) => this.selectDriver(e)} onChange={(e) => this.filterByDriverNumber(e.target?.value)} />,
                body: (driver, column) => <div className="driver-number flex flex-row gap-2 align-items-center" title="Click to see more data" onClick={() => this.toggleExpansionRow(driver)}>
                    {this.renderRowExpandIcon(driver)}
                    <span>{driver.driverNumber}</span>
                </div>
            },
            {
                field: 'driverName', header: 'Driver Name', sortable: true,
                body: (row) => <span title={row.driverName}>{row.driverName}</span>
            },
            {
                field: 'state', header: 'State', width: '70px',
                body: (row) => <span title={row.state}>{row.state}</span>
            },
            {
                field: 'totalJobsCount', header: 'Total Jobs', sortable: true, width: '70px', filter: true,
                filterElement: <div className="text-left">{this.state?.totalJobsCount}</div>,
                body: (row) => <span title={row.totalJobsCount}>{row.totalJobsCount}</span>
            },
            {
                field: 'pendingPickupCount', header: 'Pending Pickups', sortable: true, width: '70px', filter: true,
                filterElement: <div className="text-left">{this.state?.pendingPickupCount}</div>,
                body: (row) => <span title={row.pendingPickupCount}>{row.pendingPickupCount}</span>
            },
            {
                field: 'pickedUpCount', header: 'Picked Up', sortable: true, width: '70px', filter: true,
                filterElement: <div className="text-left">{this.state?.pickedUpCount}</div>,
                body: (row) => <span title={row.pickedUpCount}>{row.pickedUpCount}</span>
            },
            {
                field: 'pendingDeliveryCount', header: 'Pending Delivery', sortable: true, width: '70px', filter: true,
                filterElement: <div className="text-left">{this.state?.pendingDeliveryCount}</div>,
                body: (row) => <span title={row.pendingDeliveryCount}>{row.pendingDeliveryCount}</span>
            },
            {
                field: 'completedJobsCount', header: 'Completed', sortable: true, width: '70px', filter: true,
                filterElement: <div className="text-left">{this.state?.completedJobsCount}</div>,
                body: (row) => <span title={row.completedJobsCount}>{row.completedJobsCount}</span>
            },
            {
                field: 'pickupMinKm', header: 'KM to closest P/U', sortable: true, width: '70px',
                body: (row) => <span title={row.pickupMinKm}>{row.pickupMinKm}</span>
            },
            {
                field: 'delMinKm', header: 'KM to DEL', sortable: true, width: '70px',
                body: (row) => <span title={row.delMinKm}>{row.delMinKm}</span>
            },
            {
                field: 'kmToPickup', header: 'ETD (mins) to closest', sortable: true, width: '70px',
                body: (row) => <span title={row.kmToPickup}>{row.kmToPickup}</span>
            }
        ];

        return columns;
    }
    
    
    /**
     * @param {*} filterValues { driverNumber: number; }
     */
    setFilters(filterValues) {
        this.setState(
            { driverNumber: filterValues.driverNumber },
            () => {
                this.driversTable.filter(filterValues.driverNumber, 'driverNumber');
                this.driverFilterInput.value = filterValues.driverNumber;
                
                setTimeout(() => {
                    this.computeStatusSummaryCount();
                });
            }
        );
    }

    
    /**
     * - Driver column is using custom filter function.
     * - Run this filter whenever the user typed into the Driver column filter.
     * - Also sort the active jobs by Driver column at the same time.
     * - Emit "driverNumberFilterChanged" event so that the parent component is aware that the filter changes.
     * - Compute the status summary count.
     */
    filterByDriverNumber(filterValue) {
        this.driversTable.filter(filterValue, 'driverNumber', 'contains');
        this.driversTable.setState({ sortField: 'driverNumber', sortOrder: 1 });
        this.setState({ sortField: 'driverNumber', sortOrder: 1 }); 
        this.emitDriverNumberFilterChanged(filterValue);

        setTimeout(() => {
            this.computeStatusSummaryCount();
        });
    }


    /**
     * Emit "jobSelected" event and pass the driver details.
     * Execute only when the user pressed Enter key while inside the "Driver" column's text filter.
     * If there are multiple drivers in the filtered results, we pick the first driver and pass that in the "jobSelected" event.
     */
    selectDriver(keydownEvent) {
        // Execute only on pressing "Enter" key
        if(keydownEvent.keyCode === 13) { 
            const filters = this.driversTable.getFilters();
            const results = this.driversTable?.filterLocal(this.props?.activeJobs, filters);
            const sortedResults = this.driversTable?.sortSingle(results, 'driverNumber', 1);
            const job = sortedResults[0];

            if(job) {
                this.props?.jobSelected(job);
            }
        }
    }


    /**
     * - Collapse all expanded rows.
     * - Can be called from outside the component.
     */
    collapseAllRows() {
        this.setState({ expandedRows: [] });
    }


    /*
     * Groups the list of active jobs by driver.
     * Returns item in the list is a Driver object which has a list of ActiveJob
     * @param {*} Job[] List of active jobs
     */
    groupJobsByDriver(jobs) {
        if(!jobs?.length) {
            return [];
        }
        
        const groupedJobsByDriver = [];
        const groupedData = groupBy(jobs, 'driverNumber');

        Object.keys(groupedData)?.forEach(key => {
            const driverJobs = groupedData[key]; /* All active jobs */
            const allocatedJobs = driverJobs?.filter(job => job.status === 'Pending Pickup'); /* jobs with Pending Pickup status */
            const pickedUpJobs = driverJobs?.filter(job => (job.status === 'Picked Up' || job.status === 'Arrive P/U'));
            const completedJobs = this.apiService.loadDriverCompletedJobs(key, { timeCompletedDateFrom: new Date(), timeCompletedDateTo: new Date() }); /* List of completed jobs derived from RecentCompletedJobs API */
            const completedJobsSorted = orderBy(completedJobs, 'timeCompletedDate', 'asc');
            const totalDriverJobsCount = (driverJobs?.length ?? 0) + (completedJobsSorted?.length ?? 0); /* Count of all driver jobs including active and completed jobs for today */
            const pickupMinKm = orderBy(driverJobs, 'driverDistance')?.at(0)?.driverDistance; /* min KM value for P/U from all the Remaining Jobs only */
            const delMinKm = orderBy(driverJobs, 'deliveryDistance')?.at(0)?.deliveryDistance; /* min KM value for DEL  from all the Remaining Jobs only */
            const kmToPickup = orderBy(driverJobs, 'etd')?.at(0)?.etd; /* min ETD value from all the Remaining Jobs only */
            
            const driverData = {
                driverNumber: key,
                driverName: driverJobs?.at(0)?.driverDetail?.driverName,
                state: driverJobs?.at(0)?.stateAbbrev,
                pendingPickupCount: allocatedJobs?.length ?? 0,
                pickedUpCount: pickedUpJobs?.length ?? 0,
                pendingDeliveryCount: driverJobs?.length ?? 0,
                completedJobsCount: completedJobsSorted?.length ?? 0,
                completedJobs: completedJobsSorted,
                totalJobsCount: totalDriverJobsCount,
                pickupMinKm: pickupMinKm, 
                delMinKm: delMinKm, 
                kmToPickup: kmToPickup, 
                activeJobs: driverJobs ?? []
            };

            groupedJobsByDriver.push(driverData);
        })

        return groupedJobsByDriver;
    }


    /**
     * Get the average "Estimated Progress" for the provided list of jobs.
     */
    getAvgEstimatedProgress(jobs) {
        let sum = 0;

        jobs?.forEach(job => {
            sum = sum + (job.estimatedProgress ?? 0);
        });

        if(!sum) {
            return 0;
        } else {
            const avg = sum / jobs.length;
            return avg;
        }
    }


    /**
     * Compute the total count for allocatedJobs, pickedUpJobs, completedJobs, and remainingJobs when all rows are collapsed.
     * Do not show the counts for each status if there is an expanded row.
     */
    computeStatusSummaryCount() {
        if(this.state?.expandedRows?.length) {
            this.setState({
                pendingPickupCount: null,
                pickedUpCount: null,
                pendingDeliveryCount: null,
                completedJobsCount: null,
                totalJobsCount: null
            });
        }

        else if(!this.state?.expandedRows?.length) {
            const jobsGroupedByDriver = this.groupJobsByDriver(this.props?.activeJobs);
            const filteredDrivers = this.driversTable?.filterLocal(jobsGroupedByDriver, this.driversTable?.getFilters());
            
            let pendingPickupCount = 0;
            let pickedUpCount = 0;
            let pendingDeliveryCount = 0;
            let completedJobsCount = 0;
            let totalJobsCount = 0;

            filteredDrivers?.forEach(driver => {
                pendingPickupCount += driver.pendingPickupCount;
                pickedUpCount += driver.pickedUpCount;
                pendingDeliveryCount += driver.pendingDeliveryCount;
                completedJobsCount += driver.completedJobsCount;
                totalJobsCount += driver.totalJobsCount;
            })

            this.setState({
                pendingPickupCount,
                pickedUpCount,
                pendingDeliveryCount,
                completedJobsCount,
                totalJobsCount
            });
        }
    }


    toggleExpansionRow(driver) {
        if(this.state?.expandedRows?.length && isEqual(this.state?.expandedRows[0], driver)) {
            this.setState({ expandedRows: [] });
            this.props.driverExpanded(null);
        } else {
            this.setState({ expandedRows: [driver] });
            this.props.driverExpanded(driver);
        }

        setTimeout(() => {
            this.computeStatusSummaryCount();
        });
    }


    /**
     * Expand a driver row by driverNumber.
     * This method is supposed to be used from outside the component.
     * @param {string} driverNumber 
     */
    expandByDriverNumber(driverNumber) {
        const jobsGroupedByDriver = this.groupJobsByDriver(this.props?.activeJobs);
        const driver = jobsGroupedByDriver?.find(driver => driver.driverNumber === driverNumber);

        if(driver) {
            this.setState({ expandedRows: [driver] });
            this.props.driverExpanded(driver);
        }
    }


    rowExpansionTemplate(activeJobs) {
        return <div className="sub-table flex flex-column gap-1">
            <ActiveJobs
                activeJobs={activeJobs}
                showPagination={false}
                scrollable={false}
            ></ActiveJobs>
        </div>
    }


    renderTable() {
        if(this.props?.loadingActiveJobs) {
            return <div className="flex flex-row align-items-center gap-2 p-2">
                <Spinner color="info" />
                <em>Please wait while we retrieve your current active jobs.</em>
            </div>
        } else if(this.props?.activeJobs?.length === 0) {
            return <p><em>No active jobs. Only jobs allocated to a driver will show here.</em></p>;
        } else {
            return this.renderActiveJobsGroupedByDriver();
        }
    }


    /**
     * Render a table with expandable rows.
     * Each row contains jobs grouped by driver.
     */
    renderActiveJobsGroupedByDriver() {
        const jobsGroupedByDriver = this.groupJobsByDriver(this.props?.activeJobs);
        const driverTableColumns = this.driverTableColumns(jobsGroupedByDriver); 

        return <DataTable
            ref={(e) => this.driversTable = e} 
            value={jobsGroupedByDriver} 
            className={`p-datatable-sm p-datatable-gridlines p-datatable-striped ${this.state?.expandedRows?.length ? 'has-expanded-row':''}`} 
            paginator 
            rows={32}
            rowsPerPageOptions={[32, 50, 100]}
            expandedRows={this.state.expandedRows}
            rowExpansionTemplate={(driver) => this.rowExpansionTemplate(driver.activeJobs)}
            sortField={this.state.sortField}
            sortOrder={this.state.sortOrder}
            autoLayout={true}
            scrollable={this.props?.scrollable ?? false}
            scrollHeight={'calc(100vh - 280px)'}
            onRowClick={(e) => this.toggleExpansionRow(e.data)}
        >
            {this.renderColumns(driverTableColumns)}
        </DataTable>
    }


    renderColumns(columns) {
        const filteredColumns = columns?.filter(column => !column.hidden); /* Do not render columns with "hidden" flag */

        return filteredColumns?.map((column, index) => {
            return <Column 
                key={index} 
                field={column.field} 
                header={column.header} 
                filter={column.filter}
                filterElement={column.filterElement}
                filterMatchMode={'contains'}
                sortable={column.sortable} 
                body={column.body}
                style={{ width: column.width }}
            ></Column>
        });
    }


    renderRowExpandIcon(driver) {
        if(isEqual(driver, this.state?.expandedRows?.at(0))) {
            return <i className="pi pi-angle-up"></i>
        } else {
            return <i className="pi pi-angle-down"></i>
        }
    }


    render() {    
        return (
            <div className="active-jobs-by-driver flex flex-column gap-5">
                {this.renderTable()}
            </div>
        );
    }
}

ActiveJobsByDriver.contextType = ManagerContext;